import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { usePlatformAuth } from '../../context/PlatformAuthContext';

interface PlatformLoginFormData {
  email: string;
  password: string;
}

export function PlatformLoginPage() {
  const { login } = usePlatformAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PlatformLoginFormData>();

  const onSubmit = async (data: PlatformLoginFormData) => {
    setServerError(null);
    try {
      await login(data.email.trim(), data.password);
      navigate('/platform/tenants', { replace: true });
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      const status = (err as { response?: { status?: number } })?.response?.status;
      const ERROR_MESSAGES: Record<string, string> = {
        INVALID_CREDENTIALS: 'Email o password non corretti.',
        ACCOUNT_DISABLED: 'Account disabilitato. Contatta il supporto.',
        RATE_LIMIT_EXCEEDED: 'Troppi tentativi. Riprova tra qualche minuto.',
      };
      if (code && ERROR_MESSAGES[code]) {
        setServerError(ERROR_MESSAGES[code]);
      } else if (status === 401) {
        setServerError('Email o password non corretti.');
      } else {
        setServerError('Si è verificato un errore. Riprova.');
      }
    }
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 3 }}>
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" fontWeight={800} color="primary">
            Cantir — Super Admin
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Accedi al portale di gestione
          </Typography>
        </Box>

        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            {...register('email', {
              required: 'Email obbligatoria',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Email non valida',
              },
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
            size="small"
          />

          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            {...register('password', { required: 'Password obbligatoria' })}
            error={!!errors.password}
            helperText={errors.password?.message}
            size="small"
          />

          <Button
            type="submit"
            variant="contained"
            size="medium"
            disabled={isSubmitting}
            fullWidth
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isSubmitting ? 'Accesso in corso…' : 'Accedi'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
